"use client";

import { useState } from "react";
import Button, { ButtonVariant } from "@/app/(components)/generics/Button/Button";
import { Input, TextInputType } from "@/app/(components)/generics/inputs/default/Input/Input";
import { updateInsolePair } from "@/app/actions/insole-pair/action";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { T, TErrors } from "@/app/translations/Translations";
import { PublicInsolePair } from "@/app/types/PublicInsolePair";
import styles from "./EditInsolePair.module.css";

type Props = { lang: string; pair: PublicInsolePair; onSuccess?: () => void };

export default function EditInsolePair(props: Props) {
  const { lang, pair, onSuccess } = props;

  const { closeModal } = ModalStore();

  const [data, setData] = useState({ name: pair.name, notes: pair.notes ?? "" });
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (!data.name.trim()) {
      setFieldErrors(TErrors({ name: TKey.ERROR_REQUIRED }, lang));
      return;
    }

    setLoading(true);
    const result = await updateInsolePair(pair.uuid, { name: data.name, notes: data.notes });
    setLoading(false);

    if (result.success) {
      onSuccess?.();
      closeModal();
    } else if (result.fieldErrors) {
      setFieldErrors(TErrors(result.fieldErrors, lang));
    }
  };

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <Input
        data={data}
        dataKey="name"
        label={T(TKey.INSOLE_PAIR_NAME, lang) as string}
        lang={lang}
        required
        showRequiredLabel
        placeholder={T(TKey.INSOLE_PAIR_NAME, lang) as string}
        onChange={(d) => setData({ ...d })}
        errorMessage={fieldErrors.name}
      />

      <Input
        data={data}
        dataKey="notes"
        type={TextInputType.TEXTAREA}
        label={T(TKey.INSOLE_PAIR_NOTES, lang) as string}
        lang={lang}
        showRequiredLabel
        placeholder=""
        onChange={(d) => setData({ ...d })}
      />

      <div className={styles.actions}>
        <Button variant={ButtonVariant.SECONDARY} onClick={closeModal}>
          {T(TKey.CANCEL, lang)}
        </Button>
        <Button variant={ButtonVariant.PRIMARY} type="submit" loading={loading}>
          {T(TKey.SAVE, lang)}
        </Button>
      </div>
    </form>
  );
}
